import { Helmet } from "react-helmet-async";
import { ReactNode } from "react";

const BASE_URL = "https://www.michelelondonchanneler.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.jpg`;
const DEFAULT_OG_IMAGE_WIDTH = "1200";
const DEFAULT_OG_IMAGE_HEIGHT = "630";
const SITE_NAME = "Michele London | Channeled Author";
const DEFAULT_TITLE = "Michele London | Songs of the Mantis: Author, Spiritual Teacher & Channeler";
const DEFAULT_DESCRIPTION =
  "Michele London is a channeled author, spiritual teacher, and Reiki Master. Explore her book Songs of the Mantis, speaking topics, healing services, and more.";

/**
 * Shared by every Helmet on the site. `defer: false` commits head changes as soon as Helmet sees
 * them; the default defers the write to requestAnimationFrame, which never fires in a hidden or
 * background tab, so a page first rendered there kept index.html's home-page title and canonical.
 * `prioritizeSeoTags` puts title, canonical and OG tags first in any server-rendered head.
 */
const HELMET_PROPS = { defer: false, prioritizeSeoTags: true } as const;

interface SEOProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  children?: ReactNode;
}

/**
 * Site-wide fallbacks, rendered once in App ahead of the routes. The Helmet mounted last wins each
 * tag, so every page's <SEO> overrides these. No canonical: a route without its own SEO (404, admin)
 * is better off with none than with the home page's.
 */
export function DefaultSeo() {
  return (
    <Helmet {...HELMET_PROPS}>
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:width" content={DEFAULT_OG_IMAGE_WIDTH} />
      <meta property="og:image:height" content={DEFAULT_OG_IMAGE_HEIGHT} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={DEFAULT_TITLE} />
      <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
    </Helmet>
  );
}

export function SEO({ title, description, canonical, ogImage = DEFAULT_OG_IMAGE, children }: SEOProps) {
  const fullCanonical = canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`;

  return (
    <Helmet {...HELMET_PROPS}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={DEFAULT_OG_IMAGE_WIDTH} />
      <meta property="og:image:height" content={DEFAULT_OG_IMAGE_HEIGHT} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {children}
    </Helmet>
  );
}
